import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Stack,
  Typography,
  debounce,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import { Chart as ChartJS } from "chart.js";
import { ChartType } from "enums/report";
import { ThemeMode } from "enums/theme";
import {
  AxisMapped,
  FieldType,
  ReportDataDto,
  YAxisID,
} from "interfaces/report";
import { cloneDeep, isFunction, isNumber } from "lodash";
import { ColorPicker } from "material-ui-color";
import { useEffect, useMemo, useState } from "react";
import { convertStringToNumber, isTotalKey } from "utils/string";
import { BarChart } from "./BarChart";
import ChartSelect from "./ChartSelect";
import { DoughnutChart } from "./DoughnutChart";
import { LineChart } from "./LineChart";
import { MultiAxisChart } from "./MultiAxisChart";
import { PieChart } from "./PieChart";
import { StackedBarChart } from "./StackedBarChart";
import { StackedBarGroupChart } from "./StackedBarGroupChart";
import { StackedBarLineChart } from "./StackedBarLineChart";
import { TrendLineChart } from "./TrendLineChart";

interface Props {
  chartType?: ChartType;
  isOwner: boolean;
  reportData: ReportDataDto<AxisMapped>;
  useCircleChart: boolean;
  sheetName: string;
  handleChangeColor?: (
    key: string,
    color: string,
    axis: "xAxis" | "yAxis"
  ) => void;
  handleChangeAxisLabels?: (key: string, axis: "xAxis" | "yAxis") => void;
  print?: boolean;
  handleChangeChartType?: Function;
  handleChangeYAxisID?: (keys: string[], id: YAxisID) => void;
  handleChangeXAxis?: (key: string) => void;
  handleChangeXLabel?: (key: string) => void;
  handleChangeBarLineField?: (keys: string[], fieldType: FieldType) => void;
  onChangeAxisUnit?: (id: YAxisID, unit: string) => void;
}

export default function ReportChart({
  chartType,
  isOwner,
  handleChangeColor,
  reportData,
  handleChangeAxisLabels,
  useCircleChart,
  sheetName,
  print,
  handleChangeYAxisID,
  handleChangeChartType,
  handleChangeXAxis,
  handleChangeXLabel,
  handleChangeBarLineField, onChangeAxisUnit,
}: Props) {
  const [scale, setScale] = useState<number>(1);
  const [width, setWidth] = useState(window.innerWidth);
  const [_, setHeight] = useState(window.innerHeight);
  const [showChart, setShowChart] = useState(true);
  const theme = useTheme();
  const matchDownMD = useMediaQuery(theme.breakpoints.down("sm"));
  const sheet = useMemo(() => {
    return reportData[sheetName];
  }, [reportData, sheetName]);

  const mappedSheet = useMemo(() => {
    const clonedSheet = cloneDeep(sheet);

    clonedSheet.yAxis = clonedSheet.yAxis.filter(
      (item) => !isTotalKey(item.value)
    );

    const totalKeyIndex = clonedSheet.xAxis.findIndex((item) =>
      isTotalKey(item.value)
    );

    clonedSheet.data = clonedSheet.data.map((items) => {
      const key = items[0];
      const value = items.slice(1).map((item) => {
        const convertedValue = convertStringToNumber(item as string) as number;

        return isNumber(convertedValue) ? convertedValue : item || 0;
      });

      if (totalKeyIndex !== -1) {
        value.splice(totalKeyIndex, 1);
      }

      let indexes: number[] = [];

      clonedSheet.xAxis.forEach((item, idx) => {
        if (!item.active) {
          indexes.push(idx);
        }
      });

      indexes
        .sort((a, b) => b - a)
        .forEach((index) => {
          if (index >= 0 && index < clonedSheet.xAxis.length) {
            value.splice(index, 1);
          }
        });
      return [key, ...value];
    });

    clonedSheet.xAxis = clonedSheet.xAxis.filter((item) => {
      return !item.hide && item.active;
    });

    return clonedSheet;
  }, [sheet]);

  useEffect(() => {
    (async () => {
      chartType && setShowChart(false);

      setTimeout(() => {
        const mappedColor = {
          [ThemeMode.DARK]: { borderColor: "#ffffff5b", color: "white" },
          [ThemeMode.LIGHT]: { borderColor: "#E5E5E5", color: "#666666" },
        }[theme.palette.mode];

        ChartJS.defaults.borderColor = mappedColor.borderColor;
        ChartJS.defaults.color = mappedColor.color;

        setShowChart(true);
      }, 50);
    })();
  }, [theme.palette.mode]);

  const renderChart = () => {
    const mappedCharts = { [ChartType.LINE]: <LineChart sheet={mappedSheet} />,
      [ChartType.PIE]: <PieChart sheet={mappedSheet} />,
      [ChartType.DOUGHNUT]: <DoughnutChart sheet={mappedSheet} />,
      [ChartType.BAR]: <BarChart sheet={mappedSheet} />,
      [ChartType.TREND_LINE]: <TrendLineChart sheet={mappedSheet} />,
      [ChartType.STACKED_BAR_GROUP]: <StackedBarGroupChart sheet={mappedSheet} print={print} />,
      [ChartType.MULTI_AXIS]: <MultiAxisChart sheet={mappedSheet} />,
      [ChartType.STACKED_BAR]: <StackedBarChart sheet={mappedSheet} />,
      [ChartType.STACKED_BAR_LINE]: <StackedBarLineChart sheet={mappedSheet} />,
    }
    return mappedCharts[chartType || ChartType.LINE];
  }

  const debouncedHandleChangeColor = debounce(
    (key, color) =>
      isFunction(handleChangeColor) &&
      handleChangeColor(key, color, useCircleChart ? "xAxis" : "yAxis"),
    250
  );

  const canChangeColor = useMemo(() => {
    return isFunction(handleChangeColor) && isOwner;
  }, [isOwner]);

  useEffect(() => {
    const handleResize = debounce(() => {
      setShowChart(false);
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
      setTimeout(() => {
        setShowChart(true);
      }, 50);
    }, 100);

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleScale = (value: number) => {
    const currentScale = scale + value;

    if (currentScale > 1 || currentScale < 0.5) return;

    setScale(currentScale);
  };

  // use wrapper to reload when changing line chart  (by default not rerender)
  const handleChangeBarLineFieldWrapperFunc = (
    keys: string[],
    fieldType: FieldType
  ) => {
    setShowChart(false);

    isFunction(handleChangeBarLineField) &&
      handleChangeBarLineField(keys, fieldType);

    setTimeout(() => {
      setShowChart(true);
    }, 50);
  };

  return (
    <Grid container direction="column" spacing={4}>
      <Grid item xs>
        <ChartSelect
          chartType={chartType}
          isOwner={isOwner}
          sheet={sheet}
          handleChangeAxisLabels={handleChangeAxisLabels}
          handleChangeChartType={handleChangeChartType}
          handleChangeYAxisID={handleChangeYAxisID}
          handleChangeXAxis={handleChangeXAxis}
          handleChangeBarLineField={handleChangeBarLineFieldWrapperFunc}
          handleChangeXLabel={handleChangeXLabel}
          onChangeAxisUnit={onChangeAxisUnit}
        />
      </Grid>
      {!!chartType && (
        <Grid
          item
          xs
          style={{
            position: "relative",
          }}
        >
          <Box
            style={{
              display: "flex",
              gap: 10,
              alignItems:
                !matchDownMD && useCircleChart ? "center" : "flex-start",
              flexDirection: !matchDownMD && useCircleChart ? "row" : "column",
            }}
          >
            <Box
              style={{
                display: "flex",
                marginBottom: 20,
              }}
            >
              <Stack
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                  flexDirection: "row",
                  paddingRight: "10px",
                  justifyContent: useCircleChart ? "center" : "unset",
                }}
              >
                {(useCircleChart ? sheet.xAxis : sheet.yAxis)
                  .filter((item) => !item.hide && (useCircleChart || item.active))
                  .map((item, idx) => (
                    <Chip
                      style={{
                        marginBottom: 5,
                        marginRight: 10,
                        opacity: item.active ? 1 : 0.5,
                      }}
                      key={idx}
                      label={
                        <Typography
                          onClick={() =>
                            isFunction(handleChangeAxisLabels) &&
                            handleChangeAxisLabels(
                              item.value,
                              useCircleChart ? "xAxis" : "yAxis"
                            )
                          }
                          style={{
                            cursor: "pointer",
                            fontWeight: item.active ? 700 : "unset",
                          }}
                        >
                          {item.value}
                        </Typography>
                      }
                      className="muichip-label"
                      avatar={
                        [ChartType.STACKED_BAR_GROUP].includes(chartType) ? <div></div> : <ColorPicker
                          {...(canChangeColor ? {} : { doPopup: () => {} })}
                          value={item.color}
                          onChange={(value) =>
                            canChangeColor &&
                            debouncedHandleChangeColor(
                              item.value,
                              `#${value.hex}`
                            )
                          }
                          hideTextfield
                          disableAlpha
                        />
                      }
                    />
                  ))}
              </Stack>
            </Box>
            {chartType && showChart && (
              <Box
                style={{
                  transform: `scale(${scale})`,
                  transformOrigin: "top",
                  order: !matchDownMD && useCircleChart ? -1 : "unset",
                  maxWidth: print
                    ? useCircleChart
                      ? 600
                      : 1512
                    : useCircleChart
                    ? matchDownMD
                      ? width
                      : width * 0.3
                    : matchDownMD
                    ? width
                    : "100%",
                  maxHeight: print ? 800 : 600,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {renderChart()}
              </Box>
            )}
          </Box>
          {!print && (
            <Card
              variant="outlined"
              sx={{
                display: "flex",
                color: "text.secondary",
                position: "absolute",
                top: 30,
                right: 0,
              }}
            >
              <IconButton onClick={() => handleScale(-0.1)}>
                <ZoomOutIcon />
              </IconButton>
              <Divider orientation="vertical" variant="middle" flexItem />
              <IconButton onClick={() => handleScale(0.1)}>
                <ZoomInIcon />
              </IconButton>
            </Card>
          )}
        </Grid>
      )}
    </Grid>
  );
}
